import {
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactElement,
  KeyboardEventHandler,
} from 'react'
import _ from 'lodash'
import { StyledInput } from '../style'
import scss from './index.module.scss'

/**
 * Converts input value to a number.
 * Optionally can return 'undefined' if an empty value is allowed (otherwise returns 0)
 */
export const convertToNumber = (
  value: string | number | undefined,
  allowEmpty?: boolean
): number | undefined => {
  if (allowEmpty && !value && value !== 0) return undefined
  return _.toNumber(value)
}

const NumericInput = ({
  id,
  value,
  min = -Infinity,
  max = Infinity,
  step = 1,
  className,
  disabled = false,
  onChange,
  autoFocus = false,
  onFocus = _.noop,
  onBlur = _.noop,
  allowEmpty = false,
  testId,
}: {
  id?: string
  value?: number | string
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  className?: string
  testId?: string
  onChange?: (v: number | undefined) => void
  autoFocus?: boolean
  allowEmpty?: boolean // allows onChange(undefined)
  onFocus?: (v: number) => void
  onBlur?: (v: number) => void
}): ReactElement => {
  const inputRef = useRef(null)
  const [number, setNumber] = useState(() => convertToNumber(value, allowEmpty))

  const [isEditing, setIsEditing] = useState(false)

  const onChangeValue = useCallback(() => {
    const numberValue = convertToNumber(number, allowEmpty)
    const newValue = _.clamp(numberValue, min, max)
    onChange(convertToNumber(newValue, allowEmpty))
  }, [min, max, number, onChange, allowEmpty])

  const handleFocus = useCallback(() => {
    const newValue = inputRef.current.value
    setNumber(convertToNumber(newValue, allowEmpty))
    setIsEditing(true)
    onFocus()
  }, [onFocus, allowEmpty])

  const handleKeypress = useCallback(
    (e: KeyboardEventHandler) => {
      if (e.key === 'Enter') {
        inputRef.current.blur()
        onChangeValue()
        setIsEditing(false)
      }
    },
    [onChangeValue]
  )

  const handleBlur = useCallback(() => {
    setIsEditing(false)
    onChangeValue()
    onBlur()
  }, [onBlur, onChangeValue])

  useEffect(() => {
    if (!isEditing && inputRef.current.value !== value) {
      inputRef.current.value = value
    }
  }, [isEditing, value])

  return (
    <StyledInput
      id={id}
      ref={inputRef}
      type='number'
      disabled={disabled}
      className={`${scss.input} ${className}`}
      autoComplete='off'
      min={min}
      max={max}
      step={step}
      onKeyDown={handleKeypress}
      onInput={handleFocus}
      onClick={handleFocus}
      onBlur={handleBlur}
      onFocus={onFocus}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      data-testid={testId}
    />
  )
}

NumericInput.defaultProps = {
  value: undefined,
  min: -Infinity,
  max: Infinity,
  step: 1,
  className: '',
  testId: '',
  disabled: false,
  onChange: undefined,
  autoFocus: false,
  onBlur: _.noop,
  onFocus: _.noop,
}

export default NumericInput
